/**
 * AcoContext (P6) — which ACO the user is working in. Mirrors AuthContext's
 * practice-selection persistence pattern (session key via userStorage).
 * Fetches only when the user holds the app-plus grant.
 * (docs/reference/app-plus/IMPLEMENTATION_PLAN.md, P6)
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiClient } from '@/lib/api-client';
import { useProgramAccess } from '@/platform/ProgramAccessContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import userStorage from '@/lib/userStorage';

export interface Aco {
  id: number;
  client_id: number;
  name: string;
  aco_id_cms: string;
  /** The ACO's upload/calculation scope (D12 shadow practice). */
  shadow_practice_id?: number | null;
  /** Card metadata (viewing-year scoped) from GET /api/acos */
  roster_clinicians?: number;
  roster_tins?: number;
  config_years?: number[];
}

interface AcoContextValue {
  acos: Aco[];
  selectedAco: Aco | null;
  selectAco: (id: number) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AcoContext = createContext<AcoContextValue | null>(null);

export function AcoProvider({ children }: { children: ReactNode }) {
  const { hasProgram, loading: accessLoading } = useProgramAccess();
  const { programYear } = useProgramYear();
  const [acos, setAcos] = useState<Aco[]>([]);
  const [selectedAco, setSelectedAco] = useState<Aco | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!hasProgram('app-plus')) { setAcos([]); setSelectedAco(null); setLoading(false); return; }
    try {
      const data = await apiClient.get(`/api/acos?year=${programYear}`);
      const list: Aco[] = data?.acos ?? [];
      setAcos(list);
      const savedId = userStorage.getSessionItem('selectedAcoId');
      const saved = savedId ? list.find(a => a.id.toString() === savedId) : null;
      setSelectedAco(saved ?? (list.length === 1 ? list[0] : null));
    } catch {
      setAcos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessLoading) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, hasProgram('app-plus'), programYear]);

  const selectAco = (id: number) => {
    const aco = acos.find(a => a.id === id) ?? null;
    setSelectedAco(aco);
    if (aco) userStorage.setSessionItem('selectedAcoId', aco.id.toString());
  };

  return (
    <AcoContext.Provider value={{ acos, selectedAco, selectAco, loading, refresh }}>
      {children}
    </AcoContext.Provider>
  );
}

export function useAco(): AcoContextValue {
  const ctx = useContext(AcoContext);
  if (!ctx) throw new Error('useAco must be used within AcoProvider');
  return ctx;
}
